import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import RangeSlider from 'react-bootstrap-range-slider';
import translate from '../../../commonComponents/translate';
import CategoryProp from '../propTypes/categoryProp';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import { displayMoney } from '../../../services/utilityFunctions';
import CurrencyProp from '../../profile/propTypes/currencyProp';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import HardSkillProp from '../../challenges/propTypes/hardSkillProp';
import CourseTagProp from '../../../commonComponents/propTypes/CourseTagProp';
import PriceRangeProp from '../propTypes/PriceRangeProp';

const FILTERS = {
  CATEGORY: 'category',
  LANGUAGE: 'language',
  SKILL: 'hardSkill',
  TAGS: 'tags',
};

function AcademyFilters(props) {
  const {
    t,
    categories,
    languages,
    currencies,
    skills,
    tags,
    selected,
    onChange,
    onPriceChange,
    priceRange,
  } = props;
  const { loggedUser } = useContext(LoggedUserContext);

  const [openItems, setOpenItems] = useState([
    FILTERS.CATEGORY,
    FILTERS.LANGUAGE,
  ]);
  const toggleItem = filter => () =>
    setOpenItems(open =>
      open.includes(filter)
        ? open.filter(f => f !== filter)
        : open.concat(filter),
    );

  const handleChange = value => evt => {
    evt.preventDefault();
    onChange(value);
  };

  const handleCertificateToggle = () => {
    onChange({ certificate: selected.certificate ? undefined : true });
  };

  const allCategories = [{ _id: '', name: t('all') }].concat(categories);
  const allLanguages = [{ code: '', native: t('all') }].concat(languages);
  const allSkills = [{ _id: '', name: t('all') }].concat(skills);
  const allTags = [{ _id: '', name: t('all') }].concat(
    tags.map(tag => ({ _id: tag.tag, name: tag.tag })),
  );

  const userCurrencyCode = priceRange.currency;
  const userCurrency = currencies.find(c => c.code === userCurrencyCode) || {};

  return (
    <div className="m-portlet m-portlet--rounded m-portlet--rounded-force">
      <div className="m-portlet__body m--padding-top-5 m--padding-bottom-20 shop-sidebar">
        <div className="row">
          <div className="col-lg-12">
            <br />
            <div className="shop-sidebar-categories">
              <ul className="list-group">
                <h5>{t('academy_filters')}</h5>
                {allCategories.length > 1 && (
                  <li
                    key={FILTERS.CATEGORY}
                    className={cx(
                      'list-group-item',
                      'd-flex',
                      'justify-content-between',
                      'align-items-center',
                      { selected: openItems.includes(FILTERS.CATEGORY) },
                    )}
                    onClick={toggleItem(FILTERS.CATEGORY)}
                  >
                    {t('academy_category')}
                  </li>
                )}
                {allCategories.length > 1 && (
                  <ul
                    key={`${FILTERS.CATEGORY}-list`}
                    className="sub-list"
                    type="disc"
                  >
                    {allCategories.map(category => (
                      <li key={category._id}>
                        <a
                          href=""
                          className={cx({
                            selected: category._id === selected.category,
                          })}
                          onClick={handleChange({ category: category._id })}
                        >
                          {category.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                {allTags.length > 1 && (
                  <li
                    key={FILTERS.TAGS}
                    className={cx(
                      'list-group-item',
                      'd-flex',
                      'justify-content-between',
                      'align-items-center',
                      { selected: openItems.includes(FILTERS.TAGS) },
                    )}
                    onClick={toggleItem(FILTERS.TAGS)}
                  >
                    {t('academy_tags')}
                  </li>
                )}
                {allTags.length > 1 && (
                  <ul
                    key={`${FILTERS.TAGS}-list`}
                    className="sub-list"
                    type="disc"
                  >
                    {allTags.map(tag => (
                      <li key={tag._id}>
                        <a
                          href=""
                          className={cx({
                            selected: tag._id === selected.tags,
                          })}
                          onClick={handleChange({ tags: tag._id })}
                        >
                          {tag.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                {allLanguages.length > 1 && (
                  <li
                    key={FILTERS.LANGUAGE}
                    className={cx(
                      'list-group-item',
                      'd-flex',
                      'justify-content-between',
                      'align-items-center',
                      { selected: openItems.includes(FILTERS.LANGUAGE) },
                    )}
                    onClick={toggleItem(FILTERS.LANGUAGE)}
                  >
                    {t('academy_language')}
                  </li>
                )}
                {allLanguages.length > 1 && (
                  <ul
                    key={`${FILTERS.LANGUAGE}-list`}
                    className="sub-list"
                    type="disc"
                  >
                    {allLanguages.map(language => (
                      <li key={language.code}>
                        <a
                          href=""
                          className={cx({
                            selected: language.code === selected.language,
                          })}
                          onClick={handleChange({ language: language.code })}
                        >
                          {language.native}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                {allSkills.length > 1 && (
                  <li
                    key={FILTERS.SKILL}
                    className={cx(
                      'list-group-item',
                      'd-flex',
                      'justify-content-between',
                      'align-items-center',
                      { selected: openItems.includes(FILTERS.SKILL) },
                    )}
                    onClick={toggleItem(FILTERS.SKILL)}
                  >
                    {t('academy_skill')}
                  </li>
                )}
                {allSkills.length > 1 && (
                  <ul
                    key={`${FILTERS.SKILL}-list`}
                    className="sub-list"
                    type="disc"
                  >
                    {allSkills.map(skill => (
                      <li key={skill._id}>
                        <a
                          href=""
                          className={cx({
                            selected: skill._id === selected.hardSkill,
                          })}
                          onClick={handleChange({ hardSkill: skill._id })}
                        >
                          {skill.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </ul>
            </div>
            {priceRange?.maxPrice && (
              <div className="shop-sidebar-price">
                <h5>{t('academy_max_price')}</h5>
                <RangeSlider
                  variant="primary"
                  tooltip="off"
                  max={priceRange.max}
                  min={priceRange.min}
                  value={selected.price}
                  onChange={({ target }) =>
                    onPriceChange('price')(parseInt(target.value, 10))
                  }
                />
                <div className="price-range-values">
                  <span className="initial-value">{t('free')}</span>
                  <span className="current-value">
                    {selected.price
                      ? displayMoney(selected.price, userCurrency.symbol)
                      : t('free')}
                  </span>
                  <span className="final-value">
                    {displayMoney(priceRange.max, userCurrency.symbol)}
                  </span>
                </div>
                <br />
              </div>
            )}
            <div className="shop-sidebar-size">
              <h5>{t('academy_certificate')}</h5>
              <form className="form">
                <div className="form-group row">
                  <div className="col-9 col-form-label">
                    <div className="checkbox-list">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={selected.certificate === true}
                          onChange={handleCertificateToggle}
                        />
                        {t('academy_required')}
                        <span />
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

AcademyFilters.propTypes = {
  t: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  skills: PropTypes.arrayOf(PropTypes.shape(HardSkillProp.propType)).isRequired,
  tags: PropTypes.arrayOf(PropTypes.shape(CourseTagProp.propType)),
  selected: PropTypes.shape({
    category: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
    certificate: PropTypes.bool,
    price: PropTypes.number.isRequired,
    hardSkill: PropTypes.string.isRequired,
    tags: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onPriceChange: PropTypes.func.isRequired,
  priceRange: PropTypes.shape(PriceRangeProp.propType).isRequired,
};

AcademyFilters.defaultProps = {
  tags: [],
};

export default translate(AcademyFilters);
